import HeroBackgroundImage from 'public/images/shipsticks/hero-background-3.jpg'
import PackStep1Image from 'public/images/shipsticks/pack-step1.jpg'
import PackStep2Image from 'public/images/shipsticks/pack-step2.jpg'

import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { IconNames } from 'lib/settings/icons'

export interface PackPageContent {
  hero: NarrativeHeroProps
  newsletterSignup: {
    title: string
    subtitle: string
  }
  steps: PackStep[]
  tableData: { do: string; "don't": string }[]
}

interface PackItem {
  icon?: IconNames
  header: string | React.ReactNode
  body: string | React.ReactNode
  isRequired?: boolean
}

export interface PackStep {
  header: string | React.ReactNode
  video?: PackStepVideoType
  note?: string
  items: PackItem[]
  image?: string
}

export interface PackStepVideoType {
  header?: string | React.ReactNode
  body?: string
  caption?: string
  video: string
}

const hero = {
  backgroundImage:
    typeof HeroBackgroundImage === 'string' ? HeroBackgroundImage : HeroBackgroundImage.src,
  header: 'How to Pack and Label Your Golf Bag',
  subHeader: "It's easy to get your golf bag ready to ship–here's how.",
}

const newsletterSignup = {
  title: "Let's talk",
  subtitle:
    'Becoming a ShipSticks partner is simple, fast and completely free. ' +
    "We'll help you get set up, support your team, and provide the tools you " +
    'need to deliver a better experience from the moment guests book their trip',
}

const steps: PackStep[] = [
  {
    header: "What you'll need",
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'A soft travel bag, a hard case, or a box',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Two copies of your shipping label (printed from your account or confirmation email)',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Clear packing tape',
        isRequired: true,
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>OPTIONAL:</strong> A stiff arm and, for hard cases and soft bags, a shipping
            luggage tag (aka a fly tag) and zip tie available at most carrier stores
          </div>
        ),
        isRequired: false,
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 1:</strong> <span style={{ fontSize: '0.9em' }}>Prepare your clubs</span>
      </>
    ),
    image: typeof PackStep1Image === 'string' ? PackStep1Image : PackStep1Image.src,
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Weave a golf towel or similar between your golf heads to prevent banging.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'If you have a stiff arm, place it in your golf bag.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>Note:</strong> Some golfers like to remove any adjustable clubheads and store
            them in their golf bag for added peace of mind.
          </div>
        ),
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 2:</strong> <span style={{ fontSize: '0.9em' }}>Pack your clubs</span>
      </>
    ),
    image: typeof PackStep2Image === 'string' ? PackStep2Image : PackStep2Image.src,
    items: [
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Place your golf bag inside a soft travel bag, hard travel case, or box.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: 'Place one copy of the shipping label inside.',
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>For hard cases and soft bags:</strong> Close the case or bag, and pull any
            straps tight.
          </div>
        ),
      },
      {
        icon: 'check-circle-filled',
        header: '',
        body: (
          <div>
            <strong>For boxes:</strong> Tape the box&apos;s seams closed using at least 3 strips of
            tape at each end.
          </div>
        ),
      },
    ],
  },
  {
    header: (
      <>
        <strong>Step 3:</strong> <span style={{ fontSize: '0.9em' }}>Attach the label</span>
      </>
    ),
    video: {
      header: <span style={{ fontSize: '0.5em' }}>How to attach the label</span>,
      video: 'https://youtu.be/C5Lq-g8255o?feature=shared',
      caption: 'Video instructions on how to attach the label',
      body: '',
    },
    items: [
      {
        header: <strong>For boxes</strong>,
        body:
          'Tape the second label to the outside of the box. ' +
          'Cover the entire label with clear packing tape, pressing on it to ' +
          'ensure the label is securely attached.',
      },
      {
        header: <strong>For hard cases and soft bags</strong>,
        body: (
          <div>
            A luggage shipping tag (fly tag) works best. Slide the label into the tag making sure
            it&apos;s visible, then secure it to the bag handle with a zip tie.{' '}
            <strong>NOTE:</strong> Though a tag is preferred, you can tape the label as directed for
            boxes onto a hard case or, for soft cases, onto the flat panel at the bottom of the bag.
          </div>
        ),
      },
    ],
  },
]

const tableData = [
  {
    do: 'Do add extra padding, if you like, such as a jacket or even bubble wrap.',
    "don't": "Don't tape your label to canvas as it might fall off in transit.",
  },
  {
    do: 'Do note your clubhead settings if removing.',
    "don't": "Don't skimp on the packing tape.",
  },
  {
    do:
      'Do consider buying a reusable shipping label holder for ease if ' +
      'you ship your clubs often.',
    "don't": "Don't forget your golf wrench, especially if you removed clubheads.",
  },
]

const content: PackPageContent = {
  hero,
  newsletterSignup,
  steps,
  tableData,
}

export default content
